import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor, CapacitorCookies } from '@capacitor/core';
import { PureAbility } from '@casl/ability';
import {
	ApiResponse,
	createAbility,
	UserRecordDto,
} from '@splitsecondconnect/ultramarine-shared';
import {
	BehaviorSubject,
	catchError,
	EMPTY,
	finalize,
	firstValueFrom,
	Observable,
	switchMap,
	tap,
} from 'rxjs';

import { environment } from '../../../environment/environment';
import { WebsocketService } from '../../shared/services/web-socket.service';
import { CoreApiService } from '../api/core-api.service';

@Injectable()
export class AuthService {
	document = inject(DOCUMENT);
	#router = inject(Router);
	#http = inject(HttpClient);
	#ws = inject(WebsocketService);

	private _isAuthenticated$: BehaviorSubject<boolean> = new BehaviorSubject(
		null,
	);
	private _user$: BehaviorSubject<UserRecordDto> = new BehaviorSubject(null);

	constructor(
		private _pureAbility: PureAbility,
		private _apiService: CoreApiService,
	) {
		// Register deeplink listener for native
		App.addListener('appUrlOpen', async (event) => {
			await Browser.close();
			const url = new URL(event.url);
			const code = url.searchParams.get('code');
			if (code) {
				this.#exchangeCode(code);
			}
		});
	}

	get isAuthenticated$(): Observable<boolean> {
		return this._isAuthenticated$.asObservable();
	}

	get isAuthenticated(): boolean {
		return this._isAuthenticated$.value;
	}

	get user$(): Observable<UserRecordDto> {
		return this._user$.asObservable();
	}

	get userId(): number {
		return this._user$.value.id;
	}

	get username(): string {
		return this._user$.value.username;
	}

	get tenantName(): string {
		return this._user$.value.tenantName;
	}

	signIn(): Observable<ApiResponse<UserRecordDto>> {
		return this._apiService
			.post<ApiResponse<UserRecordDto>>(`auth/sign-in`, {})
			.pipe(
				tap((response: ApiResponse<UserRecordDto>) => {
					const user = response.data;
					if (user) {
						this._isAuthenticated$.next(true);
						this._user$.next(user);
						this.updateAbility(user.userRolePermissions);
						this.#ws.init();
					} else {
						this._isAuthenticated$.next(false);
						this._user$.next(null);
					}
				}),
				catchError((error: any) => {
					this._isAuthenticated$.next(false);
					this._user$.next(null);
					if (error.statusCode == 401) {
						this.login();
					} else {
						alert(JSON.stringify(error, null, 2));
					}
					return EMPTY;
				}),
			);
	}

	login() {
		if (Capacitor.isNativePlatform()) {
			this.#logInFromAuth();
		} else {
			this.#loginFromPortal();
		}
	}

	logout() {
		if (Capacitor.isNativePlatform()) {
			this.#logoutFromAuth();
		} else {
			this.#logoutFromPortal();
		}
	}

	updateAbility(userPermissions) {
		const ability = createAbility(userPermissions);
		this._pureAbility.update(ability.rules);
	}

	#exchangeCode(code) {
		this.#http
			.post(
				`${environment.umAuthUrl}/exchange`,
				{
					code,
				},
				{
					withCredentials: true,
				},
			)
			.pipe(
				switchMap(() => this.signIn()),
				tap(() => this.#router.navigate(['/'])),
			)
			.subscribe();
	}

	#loginFromPortal() {
		this.document.location.href = `${environment.portalUrl}?referer=${this.document.location.host}`;
	}

	#logoutFromPortal() {
		this.document.location.href = `${environment.portalUrl}/logout?referer=${this.document.location.host}`;
	}

	#logInFromAuth() {
		Browser.open({
			url: `${environment.umAuthUrl}/login?isNative=true`,
			presentationStyle: 'popover',
		});
	}

	async #logoutFromAuth() {
		this.#http
			.get(`${environment.umAuthUrl}/logout`, {
				withCredentials: true,
			})
			.pipe(
				finalize(async () => {
					await CapacitorCookies.clearAllCookies();

					// Reload the WebView
					window.location.reload();
				}),
			)
			.subscribe();
	}
}
